import { useRef } from 'react'

import { CHAT_EVENTS } from '../../constants/telemetryEvents'

// The AI chat's input telemetry, as ChatWindow logs it for its box (CHAT_TYPE, CHAT_DELETE,
// CHAT_PASTE, CHAT_SELECT_INPUT, CHAT_COPY_INPUT, same payloads), for a text box of the Code
// Coach substudy's chat: its question box and the card's answer box. `extra` goes into every
// payload (the card's box says which box it is). ChatWindow itself is left as it is.
//
// value: the box's current text. Returns the box's handlers; onChange only logs, the caller
// keeps the text.
export const useChatInputTelemetry = (value, logTelemetryEvent, extra = null) => {
  const isPasting = useRef(false)
  const lastSelection = useRef('')
  const log = (type, payload) => logTelemetryEvent?.(type, extra ? { ...payload, ...extra } : payload)

  // Only text added by typing is CHAT_TYPE; a paste is CHAT_PASTE, a deletion CHAT_DELETE.
  const onChange = (event) => {
    const next = event.target.value
    if (next.length > value.length && !isPasting.current) log(CHAT_EVENTS.TYPE, { text: next, timestamp: Date.now() })
    isPasting.current = false
  }

  // Backspace / Delete, with the text the key will leave, as ChatWindow predicts it.
  const onKeyDown = (event) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return
    const start = event.target.selectionStart
    const end = event.target.selectionEnd
    const isRangeDelete = end - start > 0
    let textAfter
    if (isRangeDelete) textAfter = value.slice(0, start) + value.slice(end)
    else if (event.key === 'Backspace') {
      if (start === 0) return
      textAfter = value.slice(0, start - 1) + value.slice(start)
    } else textAfter = value.slice(0, start) + value.slice(start + 1)
    log(CHAT_EVENTS.DELETE, {
      timestamp: Date.now(),
      cursor_position: start,
      selection_end: end,
      text_before: value,
      text_after: textAfter,
      is_range_delete: isRangeDelete,
      key_used: event.key,
    })
  }

  const onPaste = (event) => {
    isPasting.current = true
    log(CHAT_EVENTS.PASTE, { pasted_text: event.clipboardData?.getData('text') ?? '', timestamp: Date.now() })
  }

  const onSelect = (event) => {
    const selected = event.target.value.substring(event.target.selectionStart, event.target.selectionEnd)
    if (selected && selected !== lastSelection.current) {
      lastSelection.current = selected
      log(CHAT_EVENTS.SELECT_INPUT, { selected_text: selected, timestamp: Date.now() })
    } else if (!selected) {
      lastSelection.current = ''
    }
  }

  const onCopy = (event) => {
    const selected = event.target.value.substring(event.target.selectionStart, event.target.selectionEnd)
    if (selected) log(CHAT_EVENTS.COPY_INPUT, { selected_text: selected, timestamp: Date.now() })
  }

  return { onChange, onKeyDown, onPaste, onSelect, onCopy }
}

// Words as the backend counts them (ChatWindow's countWords): the text split on whitespace,
// empty parts ignored.
export const countWords = (text) => text.split(/\s+/).filter(Boolean).length

// 1042 -> "1,042"
export const formatCount = (n) => n.toLocaleString('en-US')
